import React, {ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {Button, Card} from "@varymadellc/my-weed-club-component-library";
import {QueueItemType} from "@/types";
import {apiGet, extractPayloadFromResponse} from "@/services/apiService";
import {useAuth} from "@/providers/AuthProvider";
import {useSiteMessage} from "@/providers/SiteMessageProvider";
import QueueModals from "./QueueModals";
import QueueNav from "../../components/nav/QueueNav";
import fullScreen from "@/utils/fullScreen";
import {BiExitFullscreen, BiFullscreen} from "react-icons/bi";
import cx from "classnames";
import QueueQuickShare from "@/components/QueueQuickShare";
import QueueItems from "@/components/Queue/QueueItems";
import TimeRemaining from "@/components/Queue/TimeRemaining";
import QueueHeadDisplay from "@/components/Queue/QueueHeadDisplay";
import {scrollToTop} from "@/utils/scroll";
import Input from "@/components/Input/Input";
import {useRouter} from "next/router";
import QRCode from "@/components/QRCode";
import Page from "@/layout/Page";
import Link from "next/link";

const REFRESH_QUEUE_TIME_SECONDS = 20;
const PREVIEW_MODE_REFRESH_QUEUE_TIME_SECONDS = 10;
const STALE_QUEUE_TIME_SECONDS = 600;
const QUEUE_HEAD_DISPLAY_ROTATE_SECONDS = 60;
const MIN_QUEUE_HEIGHT = 800;

export interface QueueProps {
    error?: string;
    code?: string;
    headDisplayItems?: ReactElement[];
}

let lastDataUuids: string = '';

let start = 0;

let prevQueueItem: any;

let autoScroller;

export default function Queue({code, headDisplayItems}: QueueProps) {
    const router = useRouter();

    const navTopRef = useRef<HTMLDivElement>(null);

    const [currentCode, setCurrentCode] = useState<string | undefined>(code);

    const [autoScrollerEnabled, setAutoScrollerEnabled] = useState<boolean>(false);

    const [newCode, setNewCode] = useState<string | undefined>(currentCode);

    const [inStaleMode, setInStaleMode] = useState<boolean>(false);

    const [lastDateFetch, setLastDateFetch] = useState<Date | null>();

    const theQueueRef = useRef<any>(null);

    const {user, userCan, authToken} = useAuth();

    const [expiredAt, setExpiredAt] = useState<Date | null>(null);

    const [isFullSize, setIsFullSize] = useState<boolean>(false);

    const queueRef = useRef<HTMLDivElement>(null);

    const [invalidCode, setInvalidCode] = useState<boolean>(false);

    const {setMessage} = useSiteMessage();

    const [queueItems, setQueueItems] = useState<QueueItemType[]>([]);

    const [type, setType] = useState<string>('');

    function QueueMenu({code}) {
        return (
            <div className="flex flex-row gap-2">
                {
                    code && queueRef?.current && (
                        <Button key="fullscreen" className="flex-1 ml-2" onClick={function () {
                            fullScreen(queueRef?.current)
                        }}><BiFullscreen/></Button>
                    )
                }
            </div>
        );
    }

    useEffect(() => {
        if (!lastDateFetch) return;

        const timer = setInterval(() => {
            // wait the allowed time before entering stale queue mode
            if ((lastDateFetch.getTime() + (1000 * STALE_QUEUE_TIME_SECONDS)) > Date.now()) return;

            setInStaleMode(true);

            const firstQueueItem = queueItems?.[0];

            const firstItem = document.getElementById(`queue-item-${firstQueueItem?.uuid}`);
            firstItem && firstItem.scrollIntoView({behavior: 'smooth'});

            setLastDateFetch(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, [lastDateFetch, inStaleMode]);

    useEffect(() => {
        function onFullscreenChange() {
            setIsFullSize(Boolean(document.fullscreenElement));
        }

        document.addEventListener('fullscreenchange', onFullscreenChange);

        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        const youtubeEncoded = urlParams.get('youtube');

        if (youtubeEncoded) {
            setType('youtube');
        }

        const pintrestEncoded = urlParams.get('pintrest');

        if (pintrestEncoded) {
            setType('pintrest');
        }

        const giphyEncoded = urlParams.get('giphy');

        if (giphyEncoded) {
            setType('giphy');
        }
    }, []);

    const getData = useCallback(async () => {
        if (!authToken && !currentCode) return;

        try {
            let response;

            if (currentCode) {
                response = await apiGet(`/q/code/${currentCode}`);
            } else {
                response = await apiGet('/q', authToken);
            }

            const {error, data} = await extractPayloadFromResponse(response, true);

            if (error) {
                setInvalidCode(true);

                return;
            }

            if (!data) {
                setMessage('No items found at this time');

                return;
            }

            if (currentCode && Array.isArray(data.items)) {
                setQueueItems(data.items);

                setExpiredAt(new Date(data.expirationDate));

                if (theQueueRef.current) {
                    theQueueRef.current.scrollTop = 0;
                }

                return;
            }

            setQueueItems(data);
        } catch (error: any) {
            console.error(error);
        }
    }, [authToken, setMessage, currentCode]);

    useEffect(() => {
        if (queueItems?.length) {
            const dataUuids = queueItems.map((item: QueueItemType) => item.uuid).sort().join(',');

            if (dataUuids !== lastDataUuids) {
                setInStaleMode(false);

                setLastDateFetch(new Date());

                lastDataUuids = dataUuids;
            }
        }
    }, [queueItems]);

    useEffect(() => {
        (async () => {
            await getData();
        })();
    }, [getData]);

    useEffect(() => {
        const windowHeight = window.innerHeight;

        if (!windowHeight || !navTopRef?.current || !theQueueRef?.current) return;

        const navHeight = navTopRef?.current?.offsetHeight || MIN_QUEUE_HEIGHT;

        const newHeight = windowHeight - navHeight;

        theQueueRef.current.style.height = `${newHeight >= MIN_QUEUE_HEIGHT ? newHeight : MIN_QUEUE_HEIGHT}px`;
    }, [isFullSize]);

    useEffect(() => {
        if (!navTopRef?.current) return;

        return scrollToTop(navTopRef.current);
    }, [navTopRef?.current]);

    // useEffect(() => {
    //     if (!theQueueRef?.current) return;
    //
    //     if (!code) return;
    //
    //     if (autoScroller && !inStaleMode) {
    //        clearInterval(autoScroller);
    //
    //        autoScroller = null;
    //
    //        if (prevQueueItem) {
    //             prevQueueItem.classList.remove('active-queue-item');
    //        }
    //
    //        return;
    //     }
    //
    //     const queueListItems = Array.from(theQueueRef.current?.querySelectorAll('.queue-item-marker'));
    //
    //     if (!queueListItems.length) return;
    //
    //     autoScroller = setInterval(() => {
    //         const queueItem = queueListItems[++start] as any;
    //
    //         if (!queueItem) return
    //
    //         queueItem.classList.add('active-queue-item');
    //
    //         if (!theQueueRef.current) return;
    //
    //         theQueueRef.current.scrollTo({
    //             top: queueItem.offsetTop - 50,
    //             behavior: 'smooth' // Optional: defines the transition animation
    //         });
    //
    //         if (prevQueueItem) {
    //             prevQueueItem.classList.remove('active-queue-item');
    //         }
    //
    //         prevQueueItem = queueItem;
    //     }, 3000);
    //
    //     return () => clearInterval(autoScroller);
    // }, [inStaleMode, queueItems, queueRef?.current, theQueueRef?.current]);

    const setNewCodeHandler = ({target}) => {
        setNewCode(target.value);
    }

    const updateCurrentCodeWithNewCode = async () => {
        if (!newCode) {
            setMessage('Please enter a code');

            return;
        }

        if (!router) return;

        setCurrentCode(newCode);

        await router.replace(`/q/code/${newCode}`);

        window.location.reload();
    };

    const limitedAccess = Boolean(currentCode);

    if (currentCode && invalidCode) {
        return (
            <Card className="w-4/12 m-10 !p-10 mx-auto flex flex-col justify-center items-center">
                <span>Invalid code</span>

                <p>Please Try a New Input Code</p>
                <Input onChange={setNewCodeHandler} value={newCode}/>
                <Button onClick={updateCurrentCodeWithNewCode} className="mt-2">Enter</Button>
            </Card>
        );
    }

    const toBeHiddenCallback = () => {
        if (navTopRef?.current) {
            scrollToTop(navTopRef.current);
        }

        setInStaleMode(false);
    };

    const extendedNav = (): ReactElement | null => {
        if (code || !user?.uuid || !authToken) return null;

        if (!userCan('POST_QUEUE_ACCESS_CODE')) return null;

        return (
            <>
                <QueueQuickShare key="quick-share" userUuid={user.uuid} authToken={authToken}/>
            </>
        );
    };

    return (
        <Page className={cx("w-full p-2 mx-auto bg-no-repeat bg-cover rounded-lg", {
            "bg-[url('/imgs/loungeWithPlants.png')]": !isFullSize,
            "bg-[url('/imgs/loungeWithPlants-tv.png')]": isFullSize
        })}
              menu={<div className="flex flex-1 justify-end">
                  <QueueMenu code={currentCode}/>
              </div>}>
            <div ref={queueRef}
                 className={cx("flex flex-row gap-10 max-h-full max-w-full overflow-hidden m-2", {
                     "bg-[url('/imgs/loungeWithPlants-tv.png')] bg-cover bg-no-repeat bg-center": isFullSize
                 })}>
                <div className="mx-auto max-w-full relative z-[10000]">
                    <div className="flex flex-row">
                        <div className="flex-1">
                            {
                                !isFullSize
                                && expiredAt
                                && <TimeRemaining expiredAt={expiredAt}/>
                            }
                        </div>
                        <div
                            className="fixed bottom-0 right-0 h-[1px] flex flex-1 items-end z-[1000]">
                            <div
                                className="relative w-[80px] sm:w-[120px] md:w-[140px] lg:w-[180px] z-[100001] flex flex-col p-2">
                                {
                                    code &&
                                    <div className="hidden md:flex text-center rounded m-auto relative">
                                        <QRCode width="auto"
                                                className="bg-secondary lg:p-2 text-white relative z-[10] mx-auto"
                                                description="Join the fun!" value="https://MyWeed.Club/q"/>
                                    </div>
                                }
                                {/*<div className="flex-1 bg-black p-2 rounded-lg">*/}
                                {/*    <p className="mx-auto text-center text-[7px] sm:text-sm p-0 m-0">Sponsored*/}
                                {/*        by:</p>*/}
                                {/*    <img src="/imgs/high-lounge-logo.png" alt="High Lounge"*/}
                                {/*         className="mx-auto max-w-full"/>*/}
                                {/*    <p className="text-xs mx-auto text-center text-white bg-black"><Link*/}
                                {/*        target="_blank"*/}
                                {/*        href="https://thehighlounge.com">https://thehighlounge.com</Link></p>*/}
                                {/*</div>*/}
                            </div>
                            {
                                code && (
                                    <>
                                        <div className="flex-1">&nbsp;</div>
                                        <div
                                            className="relative w-[100px] sm:w-[120px] md:w-[200px] z-[100001]">
                                            <p className="mx-auto text-center text-[7px] sm:text-sm p-0 m-0">Powered
                                                by:</p>
                                            <img src="/imgs/logo.png" alt="MyWeed.Club"
                                                 className="mx-auto max-w-full"/>
                                        </div>
                                    </>
                                )
                            }

                        </div>
                    </div>
                    {!currentCode && (
                        <div className="text-xs flex flex-row sm:flex-col gap-4 sm:gap-0">
                            <h2 className="text-lg sm:text-3xl sm:m-2 flex-2">The Queue</h2>
                            <p className="text-xs flex-1 p-2 bg-dark-black">Relight: Will requeue the item to
                                the
                                top.
                                Feel free to feed the queue! Keep it going! Choose what to post below. Queue
                                auto
                                updates
                                every {currentCode ? PREVIEW_MODE_REFRESH_QUEUE_TIME_SECONDS : REFRESH_QUEUE_TIME_SECONDS} seconds</p>
                        </div>
                    )}
                    {
                        isFullSize && (
                            <Button className="fixed top-[20px] right-[40px] z-[100]" onClick={() => {
                                document.exitFullscreen();
                            }}>
                                <BiExitFullscreen/>
                            </Button>
                        )
                    }
                    <div ref={navTopRef} className="relative max-w-full w-full">
                        <QueueNav queueItemsRef={theQueueRef}
                                  limitedAccess={limitedAccess}
                                  refreshRateInSeconds={currentCode ? PREVIEW_MODE_REFRESH_QUEUE_TIME_SECONDS : REFRESH_QUEUE_TIME_SECONDS}
                                  requestRefresh={getData}
                                  extendedNav={extendedNav()}
                                  setType={setType}/>
                    </div>
                    <QueueItems prePendedQueueItems={() => {
                        if (!currentCode || !headDisplayItems) return [];

                        return [
                            <li key={0} className="block p-0 m-0">
                                <QueueHeadDisplay toBeHiddenCallback={toBeHiddenCallback}
                                                  className="absolute top-0 left-0 right-0 z-[100] lg:h-[80vh] mx-auto"
                                                  itemRotateBySeconds={QUEUE_HEAD_DISPLAY_ROTATE_SECONDS}
                                                  hide={inStaleMode}>
                                    {headDisplayItems || []}
                                </QueueHeadDisplay>
                            </li>
                        ];
                    }} _ref={theQueueRef} queueItems={queueItems} limitedAccess={limitedAccess}/>
                </div>
            </div>
            <QueueModals requestRefresh={getData} setIsOpen={setType} type={type}
                         isOpen={Boolean(String(type).length > 0)} onClose={() => setType('')}/>
        </Page>
    )
}